import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcPaymentsV1AchQueryServiceAchGet } from '@interxfi/api-client/generated/hooks/ixc-payments-v1-ach-query-service-ach-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { getReadonlyListItemString } from '@interxfi/app-components/components/readonly-list/util';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcPaymentsV1AchState } from '@interxfi/types/generated/api';
import { PaymentsAchEntityMenu } from '../../../../../../../../../../features/payments/ach/payments-ach-entity-menu';
import { PaymentsAchEventSummary } from '../../../../../../../../../../features/payments/ach/payments-ach-event';
import { getPaymentsAchStateListItems } from '../../../../../../../../../../features/payments/ach/payments-ach-list-items';
import { getPaymentsAchDetailPath } from '../../../../../../../../../../features/payments/ach/payments-ach-routing';
import { PaymentsAchStatusPill } from '../../../../../../../../../../features/payments/ach/payments-ach-status-pill';
import { PaymentsAchSummaryBar } from '../../../../../../../../../../features/payments/ach/payments-ach-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/finance/_finance/payment/ach/_ach/$achId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ach-payment-${params.achId}`,
        label: 'ACH',
        to: getPaymentsAchDetailPath(params.achId),
        suffix: <CopyButton content={params.achId} label="Copy ACH ID" tooltip={{ copy: params.achId, copied: 'Copied ACH ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('ACH Payment'),
  component: PaymentsAchDetail,
});

function getListItems(data: IxcPaymentsV1AchState | undefined) {
  const items = getPaymentsAchStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [items.data.spec.amount, items.data.spec.programBankingName],
        },
        {
          id: 'transfer',
          title: 'Transfer',
          items: [
            ...(items.data.spec.transferType
              ? [{ ...items.data.spec.transferType.typeNamePillWithoutPrefix, title: 'Transfer Type' }]
              : [getReadonlyListItemString('transferType', 'Transfer Type', undefined)]),
            ...match(items.data.spec.transferType)
              .with({ achPush: P.not(P.nullish) }, (e) => [e.achPush.value.isSameDayRequested])
              .with({ achPull: P.not(P.nullish) }, (e) => [e.achPull.value.isSameDayRequested, e.achPull.value.debitAuthorization])
              .otherwise(() => []),
          ],
        },
        {
          id: 'source',
          title: 'Source',
          items: [
            items.data.spec.achSource.bankDetails.routingNumber,
            items.data.spec.achSource.bankDetails.accountNumber,
            { ...items.data.spec.achSource.bankDetails.accountHolderName, title: 'Name on Account' },
            items.data.spec.achSource.bankDetails.accountType,
            items.data.spec.achSource.sameDayEnabled,
          ],
        },
      ] satisfies ReadonlyListGroup[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.achId,
        items.sourceId,
        items.transferId,
        items.accountId,
        items.programId,
      ] satisfies ReadonlyListItem[],
    },
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load ACH', 'view this ACH');

function PaymentsAchDetail() {
  const { achId } = Route.useParams();
  const request = { achId } as const;
  const { data, error, isPending } = useIxcPaymentsV1AchQueryServiceAchGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.ach), [data?.ach]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <PaymentsAchStatusPill isPending={showPending} size="large" status={data?.ach?.status} />
          </HorizontalStack>

          <PaymentsAchEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <PaymentsAchSummaryBar {...request} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <PaymentsAchEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
